import type { AttachmentDto, Step } from '@process-hub/shared';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';

/**
 * Screenshots attached to steps (D-033). Images live behind the API (identity-only storage), so
 * <img src> cannot fetch them: AuthImage downloads with the bearer token and shows a blob URL.
 */

const cache = new Map<string, Promise<string>>();

function objectUrl(slug: string, id: string): Promise<string> {
  const key = `${slug}/${id}`;
  let p = cache.get(key);
  if (!p) {
    p = api.attachmentBlob(slug, id).then((b) => URL.createObjectURL(b));
    cache.set(key, p);
  }
  return p;
}

export function AuthImage({ slug, attachment, className, style, onClick }: { slug: string; attachment: AttachmentDto; className?: string; style?: React.CSSProperties; onClick?: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    objectUrl(slug, attachment.id)
      .then((u) => live && setSrc(u))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [slug, attachment.id]);
  const alt = attachment.caption ? `Screenshot ${attachment.seq}: ${attachment.caption}` : `Screenshot ${attachment.seq}`;
  if (failed) return <span className="muted">(screenshot unavailable)</span>;
  if (!src) return <span className={className} style={{ ...style, display: 'inline-block', background: 'var(--card-bg)', border: '1px solid var(--border)' }} aria-label={alt} />;
  return <img src={src} alt={alt} title={alt} className={className} style={style} onClick={onClick} />;
}

/** Thumbnails under a step on the read-only Steps tab; click to see it large. */
export function StepScreenshots({ slug, items, onOpen }: { slug: string; items: AttachmentDto[]; onOpen: (a: AttachmentDto) => void }) {
  if (!items.length) return null;
  return (
    <div className="screenshots" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
      {items.map((a) => (
        <button key={a.id} type="button" className="screenshots__thumb" style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 0, background: 'var(--ground)', cursor: 'zoom-in' }} onClick={() => onOpen(a)} aria-label={`Open screenshot ${a.seq}`}>
          <AuthImage slug={slug} attachment={a} style={{ display: 'block', width: 120, height: 76, objectFit: 'cover', borderRadius: 6 }} />
        </button>
      ))}
    </div>
  );
}

export function Lightbox({ slug, attachment, onClose }: { slug: string; attachment: AttachmentDto | null; onClose: () => void }) {
  useEffect(() => {
    if (!attachment) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachment, onClose]);
  if (!attachment) return null;
  return (
    <div role="dialog" aria-label={`Screenshot ${attachment.seq}`} onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(26, 26, 26, 0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 24, cursor: 'zoom-out' }}>
      <div style={{ maxWidth: '96vw', maxHeight: '92vh', display: 'grid', gap: 8, justifyItems: 'center' }}>
        <AuthImage slug={slug} attachment={attachment} style={{ maxWidth: '96vw', maxHeight: '84vh', objectFit: 'contain', borderRadius: 6, background: '#fff' }} />
        <div style={{ color: '#fff', fontSize: 13 }}>
          Screenshot {attachment.seq}
          {attachment.stepName ? ` · ${attachment.stepName}` : ''}
          {attachment.caption ? ` — ${attachment.caption}` : ''}
        </div>
      </div>
    </div>
  );
}

/**
 * The owner's view while a draft is open: every screenshot grouped by step, an unassigned tray,
 * move / remove, and adding an image file to a step. Changes go straight to the API (they are not
 * part of the graph save).
 */
export function ScreenshotManager({ slug, steps, items, onChanged }: { slug: string; steps: Step[]; items: AttachmentDto[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<AttachmentDto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadTo, setUploadTo] = useState<string>('');
  const real = steps.filter((s) => s.stepType !== 'start' && s.stepType !== 'end');

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The change could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    await run('upload', () => api.uploadAttachment(slug, f, uploadTo || null));
    if (fileInput.current) fileInput.current.value = '';
  };

  const tray = items.filter((a) => !a.stepKey);
  const stepOptions = (
    <>
      <option value="">— unassigned —</option>
      {real.map((s) => (
        <option key={s.key} value={s.key}>
          {s.name}
        </option>
      ))}
    </>
  );

  const row = (a: AttachmentDto) => (
    <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <button type="button" style={{ border: 0, padding: 0, background: 'transparent', cursor: 'zoom-in' }} onClick={() => setOpen(a)} aria-label={`Open screenshot ${a.seq}`}>
        <AuthImage slug={slug} attachment={a} style={{ width: 96, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', display: 'block' }} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>
          <strong>Screenshot {a.seq}</strong> <span className="muted">· {a.source === 'paste' ? 'pasted' : a.sourceRef ?? 'uploaded'}</span>
        </div>
        {a.caption && <div className="muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.caption}</div>}
      </div>
      <select aria-label={`Step for screenshot ${a.seq}`} value={a.stepKey ?? ''} disabled={busy !== null} onChange={(e) => run(a.id, () => api.setAttachmentStep(slug, a.id, e.target.value || null))}>
        {stepOptions}
      </select>
      <button type="button" className="link-button link-button--danger" disabled={busy !== null} onClick={() => window.confirm(`Remove screenshot ${a.seq}?`) && run(a.id, () => api.deleteAttachment(slug, a.id))}>
        Remove
      </button>
    </div>
  );

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card__header">
        <div className="card__title">Screenshots</div>
        <div className="card__meta">
          {items.length} attached · {tray.length} unassigned
          <label style={{ marginLeft: 16 }}>
            Add to{' '}
            <select aria-label="Step for a new screenshot" value={uploadTo} onChange={(e) => setUploadTo(e.target.value)}>
              {stepOptions}
            </select>
          </label>{' '}
          <label className="button button--secondary" style={{ cursor: 'pointer' }}>
            Add image
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden aria-label="Add a screenshot image" onChange={(e) => void onFile(e.target.files?.[0])} />
          </label>
        </div>
      </div>
      <div style={{ padding: '0 18px 12px' }}>
        {error && <div className="issue issue--error">{error}</div>}
        {items.length === 0 && <div className="muted" style={{ padding: '8px 0' }}>No screenshots yet. Attach a Scribe export or paste screenshots during the interview, or add an image here.</div>}
        {tray.length > 0 && (
          <>
            <div className="muted" style={{ marginTop: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>Unassigned — choose a step for each</div>
            {tray.map(row)}
          </>
        )}
        {real.map((s) => {
          const mine = items.filter((a) => a.stepKey === s.key);
          if (!mine.length) return null;
          return (
            <div key={s.key}>
              <div className="muted" style={{ marginTop: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>{s.name}</div>
              {mine.map(row)}
            </div>
          );
        })}
      </div>
      <Lightbox slug={slug} attachment={open} onClose={() => setOpen(null)} />
    </div>
  );
}
